import { decodeFromBase64 } from '~/utils/message';

/**
 * Normalizes a citation's text for identity comparisons (dedup/grouping keys only — never used
 * for display, the raw text is always what's rendered). The same source extracted from two
 * different documents can differ only in trailing list-separator punctuation (e.g. one document's
 * extraction ends every entry with ";", another's doesn't) even though it's the same reference.
 * @param {string} text
 * @returns {string}
 */
export const normalizeReferenceIdentity = (text = '') => text.trim().replace(/[;,.\s]+$/, '');

/**
 * The canonical per-occurrence shape produced by normalizeSecondOrderReference, used throughout
 * the citations pipeline (labeling, deduping, grouping) and by the UI (tooltip title, "Reference
 * found on", passage rendering).
 * @typedef {object} NormalizedSecondOrderReference
 * @property {string} referenceId - Physical, coordinate-based id of this occurrence (e.g.
 *   `docId:pN:cx..:cy..`); empty for synthetic primary-source/unsubstantiated entries. Unique per
 *   occurrence, not per source — never used as identity, see `text`.
 * @property {string} externalId - The second-order source's own externalId, when it has one.
 * @property {string} parentExternalId - The citing (primary) document's externalId.
 * @property {string} citationId - `${id}-${page}-${snippetIndex}` identity of the citing occurrence.
 * @property {number|null} refNum - This page's local footnote number; not reliable as identity across
 *   pages, only usable combined with referenceId to pin down one exact occurrence.
 * @property {string} text - The citation's own text — the primary identity used for grouping/dedup.
 * @property {string} name - Display name, defaults to 'Second-order reference'.
 * @property {number|null} page - Page number of the citing snippet.
 * @property {string} citationKey - Same value as citationId.
 * @property {number|null} snippetIndex - Snippet index of the citing snippet.
 * @property {boolean} unsubstantiated - True when no second-order source was found for this
 *   snippet and the document isn't flagged as a primary source.
 * @property {boolean} isPrimarySource - True when this citation resolves to the document itself
 *   (citation_metadata.is_primary_source), rather than to a distinct second-order source.
 * @property {string} doc_type - Document type, from citation_metadata.doc_type.
 * @property {string} snippet - Base64-encoded citing passage text.
 * @property {string} decodedSnippet - Decoded citing passage text.
 */

/**
 * A NormalizedSecondOrderReference promoted to a labeled "card" by buildSecondOrderReferences —
 * one per unique citation label, carrying every occurrence of that source under `passages`.
 * @typedef {NormalizedSecondOrderReference & {index: number, passages: NormalizedSecondOrderReference[]}} SecondOrderReferenceLabel
 */

const getCurrentResourceDocType = (reference = {}) => reference.citation_metadata?.doc_type || '';

const parseCitationNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

/**
 * Finds the rendered citation entry (an actual `[docId, p.N]` occurrence in the message text)
 * matching a given reference's snippetKey — i.e. confirms this snippet was actually cited, not
 * just present in the reference's data. Falls back to a doc-id+page match when the exact
 * page-snippetIndex key is unavailable (backend snippet indexes can diverge from text indexes).
 * @param {CitationEntry[]} renderedCitationEntries - Placeholders found in the message text.
 * @param {object} reference - The document reference object.
 * @param {string} snippetKey - `${page}-${snippetIndex}` key into reference.snippets.
 * @returns {CitationEntry|undefined} The matching placeholder, or undefined if this snippet was never actually cited.
 */
const getRenderedCitationEntry = (renderedCitationEntries = [], reference = {}, snippetKey = '') => {
  const externalId = reference.externalId || '';
  const [snippetPage = ''] = String(snippetKey).split('-');

  const exactMatch = renderedCitationEntries.find(({ id, snippetIndex, page }) => {
    if (`${page}-${snippetIndex}` !== snippetKey) {
      return false;
    }

    return id === externalId || `${id}-${snippetIndex}` === externalId;
  });

  if (exactMatch) {
    return exactMatch;
  }

  // Fallback: backend snippet indexes can diverge from citation text indexes.
  // Match by document id + page when exact page-index key is unavailable.
  return renderedCitationEntries.find(({ id, page }) => {
    if (String(page) !== String(snippetPage)) {
      return false;
    }

    return id === externalId || externalId.startsWith(`${id}-`);
  });
};

/**
 * Finds the document reference object for a citation by externalId, allowing for the
 * `externalId-snippetIndex` id shape as well as the bare externalId.
 * @param {{references: object[], id: string, snippetIndex: string}} params - references to search,
 *   plus the citation's document id and snippet index.
 * @returns {object|undefined} The matching reference object, or undefined if not found.
 */
const findCitationReference = ({ references = [], id = '', snippetIndex = '0' } = {}) =>
  references.find((ref) => ref?.externalId === id || ref?.externalId === `${id}-${snippetIndex}`);

/**
 * The raw per-snippet second-order reference map for a document. Keyed by
 * `${page}-${snippetIndex}`; each value is either an array of {referenceId, refNum, text}
 * entries, an empty array (checked, nothing found — unsubstantiated), or the key is simply
 * absent (never checked for that snippet).
 * @param {object} reference - The document reference object.
 * @returns {Record<string, object[]>} Map of `${page}-${snippetIndex}` → raw second-order entries.
 */
export const getSecondOrderReferencesBySnippet = (reference = {}) => reference?.second_order_references_by_snippet || {};

/**
 * Every genuine second-order source cited anywhere within a document, deduped by identity (the
 * same source repeated across several of the document's own snippets collapses to one entry) —
 * used by the "add reference" popup to list a parent document's nested sources alongside it, not
 * just the parent document itself. Each entry keeps the page/snippetIndex of the citing snippet it
 * was found under (its first occurrence), so selecting it can insert a citation anchored to where
 * that source actually appears in the parent document.
 * @param {object} reference - The document reference object.
 * @returns {{referenceId: string, text: string, page: number, snippetIndex: number}[]}
 */
export const getDocumentSecondOrderReferences = (reference = {}) => {
  const bySnippet = getSecondOrderReferencesBySnippet(reference);
  const seen = new Set();
  const result = [];

  Object.entries(bySnippet).forEach(([snippetKey, entries]) => {
    if (!Array.isArray(entries) || !entries.length) return;

    const [page, snippetIndex] = snippetKey.split('-').map(Number);

    entries.forEach((entry) => {
      const identity = normalizeReferenceIdentity(entry.text) || entry.referenceId || '';
      if (!identity || seen.has(identity)) return;
      seen.add(identity);
      result.push({ ...entry, page, snippetIndex });
    });
  });

  return result;
};

/**
 * Every genuine second-order source cited anywhere across a set of documents, deduped *globally*
 * by identity — the same source cited by more than one parent document (e.g. two ads both citing
 * the same SPC) collapses into a single entry, the same way the printed reference list/Preview
 * numbering already treats it as one shared citation rather than one per citing document (see
 * mapReferencesFromBullets in footnotes.js and transformDocReferences's resolveSpanNums below).
 * Used by the "add reference" popup so a shared source is only listed once, under whichever parent
 * document it's first found under, instead of once per parent.
 * @param {object[]} references - Document reference objects (the footnotes list). User-added
 *   (`addedByUser`) entries are skipped — they never carry second-order backend data.
 * @returns {{key: string, text: string, referenceId: string, page: number, snippetIndex: number,
 *   primaryReference: object, documents: object[]}[]} One entry per unique source, `documents`
 *   listing every parent document (in encounter order) that cites it.
 */
export const getAllSecondOrderReferences = (references = []) => {
  const byKey = new Map();

  references.forEach((reference) => {
    if (reference?.addedByUser) return;

    getDocumentSecondOrderReferences(reference).forEach((entry) => {
      const key = normalizeReferenceIdentity(entry.text) || entry.referenceId || '';
      if (!key) return;

      const existing = byKey.get(key);
      if (existing) {
        const referenceId = reference.externalId || reference.id;
        if (!existing.documents.some((doc) => (doc.externalId || doc.id) === referenceId)) {
          existing.documents.push(reference);
        }
        return;
      }

      byKey.set(key, { ...entry, key, primaryReference: reference, documents: [reference] });
    });
  });

  return Array.from(byKey.values());
};

/**
 * Whether a document is flagged as a primary source (`citation_metadata.is_primary_source`) — a
 * single per-document flag, not per-snippet. When true, a citation with no second-order data for
 * its snippet is treated as "cites the document itself" rather than "unsubstantiated."
 * @param {object} reference - The document reference object.
 * @returns {boolean}
 */
const isPrimarySourceDocument = (reference = {}) => Boolean(reference.citation_metadata?.is_primary_source);

/**
 * Whether a specific citation (id+page+snippetIndex) is unsubstantiated: no second-order
 * reference is available for that citing snippet — either the backend explicitly checked and
 * found none (an empty array) or never recorded the key at all — and the document isn't flagged
 * as a primary source.
 * @param {{references: object[], id: string, snippetIndex: string, page: string}} params - references
 *   to search, plus the citation's document id, snippet index, and page.
 * @returns {boolean}
 */
export const isUnsubstantiatedCitation = ({ references = [], id = '', snippetIndex = '0', page = '' } = {}) => {
  const reference = findCitationReference({ references, id, snippetIndex });
  const bySnippet = getSecondOrderReferencesBySnippet(reference);
  const snippetKey = `${page}-${snippetIndex}`;

  const hasNoSecondOrderData = !Array.isArray(bySnippet[snippetKey]) || bySnippet[snippetKey].length === 0;

  return hasNoSecondOrderData && !isPrimarySourceDocument(reference);
};

/**
 * Resolves what "source" to show for a given citing snippet: real second-order reference entries
 * if the backend recorded any for this exact snippet, otherwise a single synthetic entry
 * representing `reference` itself — flagged `isPrimarySource: true` when the document is a
 * primary source, or `unsubstantiated: true` otherwise. Shared by chat (buildSecondOrderReferencesBySnippet)
 * and authoring (transformDocReferences/renderCitationBulletsOverIframe) so both resolve identically.
 * @param {{reference: object, snippetKey: string, currentResourceDocType: string}} params - the
 *   citing document reference, its `${page}-${snippetIndex}` key, and its resolved doc_type.
 * @returns {object[]} Always non-empty: either the real second-order entries recorded for this
 *   snippet, or exactly one synthetic entry ({referenceId: null, text, isPrimarySource, unsubstantiated}).
 */
export const getSecondOrderReferenceSource = ({ reference = {}, snippetKey = '', currentResourceDocType = '' } = {}) => {
  const bySnippet = getSecondOrderReferencesBySnippet(reference);
  const snippetSpecificSource = Array.isArray(bySnippet[snippetKey]) ? bySnippet[snippetKey] : null;

  if (snippetSpecificSource?.length) {
    return snippetSpecificSource;
  }

  const isPrimarySource = isPrimarySourceDocument(reference);

  return [
    {
      externalId: reference.externalId,
      refNum: null,
      text: reference.name,
      isPrimarySource,
      unsubstantiated: !isPrimarySource,
      doc_type: currentResourceDocType,
    },
  ];
};

/**
 * Normalizes a raw second-order entry (from getSecondOrderReferenceSource) into the canonical
 * shape used throughout the citations pipeline and the UI (title/label/passage rendering).
 * @param {object} entry - Raw entry: {referenceId, refNum, text, unsubstantiated?, isPrimarySource?, ...}.
 * @param {string} snippet - Base64-encoded citing passage text (reference.snippets[snippetKey]).
 * @param {number|null} page - Page number of the citing snippet.
 * @param {number|null} snippetIndex - Snippet index of the citing snippet.
 * @param {string} citationKey - `${id}-${page}-${snippetIndex}` identity of the citing occurrence.
 * @returns {NormalizedSecondOrderReference}
 */
const normalizeSecondOrderReference = (
  entry = {},
  snippet = '',
  page = null,
  snippetIndex = null,
  citationKey = '',
  useNewAgent = false,
) => ({
  referenceId: entry.referenceId || entry.reference_id || '',
  externalId: entry.externalId || entry.parentExternalId || entry.currentExternalId || '',
  parentExternalId: entry.parentExternalId || entry.currentExternalId || '',
  citationId: entry.citationId || citationKey,
  refNum: entry.refNum || entry.ref_num || null,
  text: entry.text || '',
  name: entry.name || 'Second-order reference',
  page,
  citationKey,
  snippetIndex,
  unsubstantiated: Boolean(entry.unsubstantiated),
  isPrimarySource: Boolean(entry.isPrimarySource),
  doc_type: entry.doc_type || '',
  snippet,
  decodedSnippet: useNewAgent ? snippet || '' : snippet ? decodeFromBase64(snippet) : '',
});

/**
 * Builds, per citing snippet, the list of normalized second-order reference items for a whole
 * message. Only snippets that were actually cited in the text (per renderedCitationEntries) are
 * included. Deduped per-snippet by citation text (falling back to referenceId) — see the
 * dedupeKeyFor comment below for why refNum isn't part of that identity.
 * @param {{references: object[], renderedCitationEntries: CitationEntry[], useNewAgent: boolean}} params -
 *   Document references for the message, every citation placeholder actually rendered in its
 *   text, and whether snippet text is already decoded (new-agent) vs base64-encoded (legacy).
 * @returns {Record<string, NormalizedSecondOrderReference[]>} Map of `${page}-${snippetIndex}` →
 *   normalized items. A key can map to an empty array (the snippetKey was cited but
 *   getSecondOrderReferenceSource's guaranteed-non-empty result was still filtered out below) —
 *   in practice this shouldn't currently happen, since getSecondOrderReferenceSource never
 *   returns an empty array, but the `if (!source.length)` guard below assumes it can.
 */
export const buildSecondOrderReferencesBySnippet = ({
  references = [],
  renderedCitationEntries = [],
  useNewAgent = false,
} = {}) =>
  references.reduce((acc, reference = {}) => {
    const snippets = reference.snippets || {};
    const bySnippet = getSecondOrderReferencesBySnippet(reference);
    const externalId = reference.externalId || '';
    const renderedKeys = renderedCitationEntries
      .filter(({ id, snippetIndex }) => id === externalId || `${id}-${snippetIndex}` === externalId)
      .map(({ page, snippetIndex }) => `${page}-${snippetIndex}`);
    const snippetKeys = [...new Set([...Object.keys(snippets), ...Object.keys(bySnippet), ...renderedKeys])];
    const currentResourceDocType = getCurrentResourceDocType(reference);

    snippetKeys.forEach((snippetKey) => {
      const renderedCitationEntry = getRenderedCitationEntry(renderedCitationEntries, reference, snippetKey);
      if (!renderedCitationEntry) {
        return;
      }

      if (!acc[snippetKey]) {
        acc[snippetKey] = [];
      }

      const [snippetPage = '1', snippetIndex = '0'] = String(snippetKey).split('-');
      const parsedPage = parseCitationNumber(snippetPage, 1);
      const citationKey = `${renderedCitationEntry.id}-${renderedCitationEntry.page}-${renderedCitationEntry.snippetIndex}`;
      const source = getSecondOrderReferenceSource({
        reference,
        snippetKey,
        currentResourceDocType,
      });

      if (!source.length) {
        return;
      }

      const normalized = source
        .map((entry) =>
          normalizeSecondOrderReference(
            {
              ...entry,
              parentExternalId: reference.externalId,
              currentExternalId: reference.externalId,
              name: reference.name,
              doc_type: currentResourceDocType,
              citationId: citationKey,
            },
            snippets[snippetKey] || '',
            parsedPage,
            parseCitationNumber(snippetIndex, 0),
            citationKey,
            useNewAgent,
          ),
        )
        .filter((item) => item.referenceId || item.text);

      if (!normalized.length) {
        return;
      }

      // Keyed by text (not refNum): refNum is that page's own local reference-list numbering and
      // isn't guaranteed stable for the same source, so requiring it to match too could let a
      // genuine duplicate slip through as a second "Passage N" row downstream. text is preferred,
      // falling back to referenceId only when text is missing — same identity rule used
      // everywhere else this source gets deduped/grouped/numbered.
      const dedupeKeyFor = (item) => `${item.externalId}-${item.text || item.referenceId || ''}`;
      const existingKeys = new Set(acc[snippetKey].map(dedupeKeyFor));

      normalized.forEach((item) => {
        const dedupeKey = dedupeKeyFor(item);
        if (!existingKeys.has(dedupeKey)) {
          acc[snippetKey].push(item);
          existingKeys.add(dedupeKey);
        }
      });
    });

    return acc;
  }, {});

/**
 * Builds the final, ordered list of second-order reference "cards" for a message: one per unique
 * citation label (from citationLabelDict, the same numbering rendered as bullets in the text), each
 * carrying every passage where that source was cited (`.passages`) so a source cited more than once,
 * or at multiple physical locations, still shows every occurrence under a single label.
 * @param {object} message - Unused directly but kept for parity with authoring's equivalent resolvers.
 * @param {Record<string, NormalizedSecondOrderReference[]>} secondOrderReferencesBySnippet - From buildSecondOrderReferencesBySnippet.
 * @param {Record<string, number>} citationLabelDict - From buildRefNumCitationDict, same key scheme.
 * @returns {SecondOrderReferenceLabel[]} One entry per label, sorted ascending.
 */
export const buildSecondOrderReferences = (message, secondOrderReferencesBySnippet = {}, citationLabelDict = {}) => {
  const allItems = Object.values(secondOrderReferencesBySnippet)
    .flat()
    .filter((item) => item && item.externalId);

  // One entry per unique citation label — dictated by citationLabelDict (same source of truth as
  // what is rendered in the text). This guarantees the References list count matches exactly.
  return Object.entries(citationLabelDict)
    .sort(([, a], [, b]) => a - b)
    .map(([dictKey, label]) => {
      // Match by ${externalId}-${text||referenceId} (second-order) or ${externalId}-${snippetIndex}
      // (fallback). Grouping by text (not referenceId) means the same source appearing at multiple
      // physical locations (e.g. a repeated footer reference list) collapses into one label instead
      // of being treated as distinct references. filter (not find) so that the same source appearing
      // in multiple snippets is collected as multiple passages under a single label.
      const matchingItems = allItems.filter((item) =>
        item.referenceId
          ? dictKey === `${item.externalId}-${item.text || item.referenceId}`
          : dictKey === `${item.externalId}-${item.snippetIndex}`,
      );

      if (!matchingItems.length) return null;

      const primary = matchingItems[0];
      return {
        ...primary,
        index: label,
        citationId: primary.citationId || primary.citationKey || dictKey,
        citationKey: primary.citationKey || primary.citationId || dictKey,
        referenceId: primary.referenceId,
        externalId: primary.parentExternalId || primary.externalId,
        passages: matchingItems,
      };
    })
    .filter(Boolean);
};
